import { lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { trackPageView } from './services/telemetry';

// Route-level code splitting — lazy-loaded pages
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const DiskImagesPage = lazy(() =>
  import('./pages/DiskImagesPage').then(m => ({ default: m.DiskImagesPage }))
);
const SandboxesPage = lazy(() =>
  import('./pages/SandboxesPage').then(m => ({ default: m.SandboxesPage }))
);
const SandboxDetailPage = lazy(() =>
  import('./pages/SandboxDetailPage').then(m => ({ default: m.SandboxDetailPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage }))
);
const DocsPage = lazy(() => import('./pages/DocsPage').then(m => ({ default: m.DocsPage })));
const PoliciesPage = lazy(() =>
  import('./pages/PoliciesPage').then(m => ({ default: m.PoliciesPage }))
);
const VolumesPage = lazy(() =>
  import('./pages/VolumesPage').then(m => ({ default: m.VolumesPage }))
);

/** Tracks page views on route changes via Application Insights. */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, location.pathname);
  }, [location.pathname]);
  return null;
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <PageViewTracker />
            <Routes>
              {/* Public route — MSAL redirect URI */}
              <Route path="/signin" element={<LoginPage />} />

              {/* Protected routes */}
              <Route element={<AuthGuard />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/images" element={<DiskImagesPage />} />
                  <Route path="/sandboxes" element={<SandboxesPage />} />
                  <Route path="/sandboxes/:sandboxId" element={<SandboxDetailPage />} />
                  <Route path="/policies" element={<PoliciesPage />} />
                  <Route path="/volumes" element={<VolumesPage />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/disk-images" element={<Navigate to="/images" replace />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </BrowserRouter>

          {/* Toast notifications — theme-aware via Tailwind classes */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              className:
                '!bg-white dark:!bg-mica-card !text-surface-900 dark:!text-surface-100 ' +
                '!border !border-surface-200 dark:!border-white/[0.08] ' +
                '!rounded-fluent-md !shadow-elevation-8 dark:!shadow-none ' +
                '!text-[13px] !font-sans',
              style: {},
              success: {
                iconTheme: {
                  primary: '#107C10',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#E81123',
                  secondary: '#fff',
                },
              },
            }}
          />

          {/* React Query DevTools */}
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
